import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BrandMark } from '../components/BrandMark';
import { useAuth } from '../contexts/AuthContext';
import {
  ActiveTurn,
  ApiError,
  Conversation,
  ConversationSummary,
  getActiveTurn,
  getConversation,
  getQuestionBalance,
  getTurnStatus,
  listConversations,
  submitQuestion,
} from '../lib/api';

const ACTIVE_TURN_KEY = 'sanjeom_ai_active_turn';

type ActiveTurnPointer = {
  sessionId: string;
  turnId: string;
  message: string;
};

function readActiveTurn(): ActiveTurnPointer | null {
  try {
    const value = JSON.parse(localStorage.getItem(ACTIVE_TURN_KEY) || 'null') as Partial<ActiveTurnPointer> | null;
    if (!value?.sessionId || !value.turnId) return null;
    return {
      sessionId: String(value.sessionId),
      turnId: String(value.turnId),
      message: String(value.message || ''),
    };
  } catch {
    return null;
  }
}

function writeActiveTurn(value: ActiveTurnPointer | null): void {
  if (!value) {
    localStorage.removeItem(ACTIVE_TURN_KEY);
    return;
  }
  localStorage.setItem(ACTIVE_TURN_KEY, JSON.stringify(value));
}

function createClientTurnKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `turn-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function sessionTitle(item: ConversationSummary): string {
  return String(item.title || 'Untitled conversation').trim() || 'Untitled conversation';
}

export function WorkspacePage() {
  const { user, loading: authLoading, logout } = useAuth();
  const location = useLocation();
  const [draft, setDraft] = useState('');
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [activeTurn, setActiveTurn] = useState<ActiveTurnPointer | null>(() => readActiveTurn());
  const [balance, setBalance] = useState<number | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const pollingTimer = useRef<number | null>(null);

  const refreshSidebar = useCallback(async () => {
    const [items, wallet] = await Promise.all([listConversations(), getQuestionBalance()]);
    setConversations(items);
    setBalance(Math.max(0, Number(wallet.questions_remaining || 0)));
  }, []);

  const openConversation = useCallback(async (sessionId: string) => {
    setError('');
    setSelectedSessionId(sessionId);
    const detail = await getConversation(sessionId);
    setConversation(detail);
    const running = detail.active_turn as ActiveTurn | null | undefined;
    if (running?.turn_id) {
      const pointer = { sessionId, turnId: running.turn_id, message: running.message };
      setActiveTurn(pointer);
      writeActiveTurn(pointer);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      writeActiveTurn(null);
      setActiveTurn(null);
      if (!authLoading) setInitialLoading(false);
      return;
    }
    let active = true;
    const restore = async () => {
      try {
        await refreshSidebar();
        let pointer = readActiveTurn();
        if (!pointer) {
          const serverActive = await getActiveTurn();
          if (serverActive) {
            pointer = {
              sessionId: serverActive.session_id,
              turnId: serverActive.turn_id,
              message: serverActive.message,
            };
            writeActiveTurn(pointer);
          }
        }
        if (pointer) {
          try {
            const detail = await getConversation(pointer.sessionId);
            if (active) {
              setSelectedSessionId(pointer.sessionId);
              setConversation(detail);
              setActiveTurn(pointer);
            }
          } catch {
            writeActiveTurn(null);
            if (active) setActiveTurn(null);
          }
        }
      } catch (restoreError) {
        if (active) setError(restoreError instanceof Error ? restoreError.message : 'Workspace could not be loaded.');
      } finally {
        if (active) setInitialLoading(false);
      }
    };
    void restore();
    return () => { active = false; };
  }, [authLoading, refreshSidebar, user]);

  useEffect(() => {
    if (!activeTurn || !user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const status = await getTurnStatus(activeTurn.sessionId, activeTurn.turnId);
        if (cancelled) return;
        if (status.status === 'completed' || status.status === 'failed') {
          writeActiveTurn(null);
          await refreshSidebar();
          if (selectedSessionId === activeTurn.sessionId || !selectedSessionId) {
            const detail = await getConversation(activeTurn.sessionId);
            if (!cancelled) {
              setSelectedSessionId(activeTurn.sessionId);
              setConversation(detail);
            }
          }
          if (status.status === 'failed') {
            setError(status.error_message || 'The response could not be completed. Your question was returned.');
          }
          if (!cancelled) setActiveTurn(null);
          return;
        }
        pollingTimer.current = window.setTimeout(poll, 1500);
      } catch (pollError) {
        if (cancelled) return;
        setError(pollError instanceof Error ? pollError.message : 'Connection interrupted. Retrying…');
        pollingTimer.current = window.setTimeout(poll, 3000);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (pollingTimer.current !== null) window.clearTimeout(pollingTimer.current);
    };
  }, [activeTurn, refreshSidebar, selectedSessionId, user]);

  const messages = useMemo(() => {
    const items = [...(conversation?.messages || [])];
    if (activeTurn?.message && !items.some((item) => item.role === 'user' && item.content === activeTurn.message)) {
      items.push({ role: 'user', content: activeTurn.message, created_at: new Date().toISOString() });
    }
    return items;
  }, [activeTurn, conversation]);

  if (authLoading) return <div className="workspace-loading">Opening your workspace…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  const startNew = () => {
    if (activeTurn) {
      setError('Your current response is still being prepared.');
      return;
    }
    setSelectedSessionId(null);
    setConversation(null);
    setDraft('');
    setError('');
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || sending || activeTurn) return;
    if ((balance ?? 0) < 1) {
      setError('No questions remaining. Choose a question pack to continue.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const result = await submitQuestion({
        sessionId: selectedSessionId,
        message,
        clientTurnKey: createClientTurnKey(),
      });
      const pointer = { sessionId: result.session_id, turnId: result.turn_id, message };
      writeActiveTurn(pointer);
      setActiveTurn(pointer);
      setSelectedSessionId(result.session_id);
      setDraft('');
      if (!conversation || conversation.session_id !== result.session_id) {
        setConversation({
          session_id: result.session_id,
          title: message.slice(0, 40),
          status: 'active',
          messages: [],
          active_turn: {
            turn_id: result.turn_id,
            status: result.status === 'running' ? 'running' : 'queued',
            message,
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Your question could not be submitted.');
      if (sendError instanceof ApiError && sendError.code === 'QUESTION_BALANCE_EXHAUSTED') setBalance(0);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="workspace-page">
      <aside className="workspace-sidebar">
        <BrandMark />
        <button className="new-thread" type="button" onClick={startNew}>+ New conversation</button>
        <div className="thread-list">
          <p className="sidebar-label">Conversations</p>
          {initialLoading ? <div className="thread-empty">Loading conversations…</div> : null}
          {!initialLoading && conversations.length === 0 ? (
            <div className="thread-empty">Your completed conversations will appear here.</div>
          ) : null}
          {conversations.map((item) => (
            <button
              type="button"
              className={`thread-item${selectedSessionId === item.session_id ? ' thread-item--active' : ''}`}
              key={item.session_id}
              onClick={() => void openConversation(item.session_id)}
            >
              <strong>{sessionTitle(item)}</strong>
              <span>{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : ''}</span>
            </button>
          ))}
        </div>
        <nav className="workspace-sidebar__footer" aria-label="Workspace links">
          <Link to="/pricing">Question packs</Link>
          <Link to="/support">Support</Link>
          <button type="button" onClick={logout}>Sign out</button>
          <Link to="/">Back to site</Link>
        </nav>
      </aside>
      <main className="workspace-main">
        <header className="workspace-header">
          <Link className="workspace-header__mobile-brand" to="/" aria-label="Sanjeom home">Sanjeom</Link>
          <div>
            <p className="eyebrow">Private workspace</p>
            <h1>{conversation?.title || 'New conversation'}</h1>
          </div>
          <Link className="balance-pill" to="/pricing">
            {balance === null ? 'Loading balance…' : `${balance} question${balance === 1 ? '' : 's'} remaining`}
          </Link>
        </header>
        <section className={`workspace-conversation${messages.length ? ' workspace-conversation--active' : ''}`} aria-live="polite">
          {messages.length === 0 ? (
            <div className="empty-conversation">
              <div className="empty-conversation__mark"><span /></div>
              <h2>What needs a clearer look?</h2>
              <p>Describe the situation in your own words. Include the constraints, the uncertainty and what you have already tried.</p>
              <div className="prompt-starters">
                <button type="button" onClick={() => setDraft('Help me compare two options without assuming either is obviously right.')}>Compare two options</button>
                <button type="button" onClick={() => setDraft('Help me prepare for a difficult conversation and identify what I should not leave unsaid.')}>Prepare a conversation</button>
                <button type="button" onClick={() => setDraft('I feel stuck. Help me identify the smallest useful next step.')}>Find the next step</button>
              </div>
            </div>
          ) : (
            <div className="conversation-thread">
              {messages.map((item, index) => (
                <article className={`conversation-message conversation-message--${item.role}`} key={`${item.created_at}-${index}`}>
                  <span>{item.role === 'user' ? 'You' : 'Sanjeom'}</span>
                  <div>
                    {item.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
                    ) : item.content}
                    {item.image_url ? (
                      <img className="conversation-message__image" src={item.image_url} alt="Generated visual" />
                    ) : null}
                  </div>
                </article>
              ))}
              {activeTurn ? (
                <div className="assistant-thinking" role="status">
                  <i /><i /><i />
                  <span>Working through your question…</span>
                </div>
              ) : null}
            </div>
          )}
        </section>
        <form className="workspace-composer" onSubmit={send}>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Write what is on your mind…"
            rows={3}
            maxLength={300}
            disabled={Boolean(activeTurn)}
          />
          {error ? <p className="workspace-error" role="alert">{error}</p> : null}
          <div className="workspace-composer__footer">
            <span>AI responses can be incomplete. Use your judgement for important decisions.</span>
            {(balance ?? 0) < 1 ? (
              <Link className="button button--warm button--small" to="/pricing">Get questions</Link>
            ) : (
              <button className="button button--ink button--small" type="submit" disabled={!draft.trim() || sending || Boolean(activeTurn)}>
                {sending ? 'Submitting…' : activeTurn ? 'Response in progress' : 'Send question'}
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}
